import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Empty from 'antd/lib/empty';
import Layout from 'antd/lib/layout';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { PartitionOutlined, PlusOutlined } from '@ant-design/icons';
import { ReleaseNav } from '@/types';
import { AndroidDependenciesConfig } from '@/types/androidDependencies';
import { IosDependenciesConfig } from '@/types/iosDependencies';
import { DependenciesReleaseReport } from '@/types/dependencies';
import { Destination } from '@/types/destinations';
import { RootState } from '@/reducers/reducers';
import {
    selectAppSettings,
    selectAuthCredentials,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsReleaseCompleted,
    selectIsReleaseKickedOff,
    selectWorkflowId,
    selectWorkflowRunId,
} from '@/selectors/selectors';
import { useGetDependenciesQuery } from '@/middlewares/defaultApi';
import { trackUserEvent } from '@/actions/trackEvent';
import { Event } from '@/utils/analytics';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import useSetLastViewedReleaseStep from '@/components/hooks/useSetLastViewedReleaseStep';
import AndroidDependenciesPanelBody from '@/components/dependencies/AndroidDependenciesPanelBody';
import ConfigureDependenciesModal from '@/components/dependencies/ConfigureDependenciesModal';
import IosDependenciesPanelBody from '@/components/dependencies/IosDependenciesPanelBody';
import {
    buildDestinationHeaderLabels,
    getDestinationId,
    isAndroidDependenciesDestination,
    isIosDependenciesDestination,
    pluralize,
} from '@/components/dependencies/dependenciesUtils';
import * as androidUtils from '@/components/dependencies/androidDependenciesUtils';
import * as iosUtils from '@/components/dependencies/iosDependenciesUtils';
import { titleStyle } from '@/components/styles/globalStyles';

type PanelEntry = {
    key: string;
    destinationId: string;
    status?: 'parsing' | 'success' | 'failed';
    errorMessage?: string;
    updates: number;
    total: number;
    hasReport: boolean;
    body: React.ReactNode;
};

const DependenciesPage: React.FC<ReleaseComponentProps> = ({ orgId, appId, releaseId }) => {
    useSetLastViewedReleaseStep(releaseId, ReleaseNav.Dependencies);

    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(trackUserEvent({ orgId, appId, eventName: Event.ViewDependencies }));
    }, [dispatch, orgId, appId]);

    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, appId));
    const isReleaseCompleted = useSelector((state: RootState) => selectIsReleaseCompleted(state, releaseId));
    const isReleaseKickedOff = useSelector((state: RootState) => selectIsReleaseKickedOff(state, releaseId));
    const configureDisabled = isReleaseCompleted || !isReleaseKickedOff;
    const configureDisabledTooltip =
        isReleaseCompleted ? 'This release is completed — dependencies cannot be updated'
        : !isReleaseKickedOff ? 'Kick off this release before configuring dependencies'
        : '';
    const updateDisabledTooltip =
        configureDisabled ? 'Update the configuration in a kicked off uncompleted release' : '';

    const destinationsByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const destinations: Destination[] = React.useMemo(
        () =>
            Object.values(destinationsByStepConfigId).filter(
                (destination) =>
                    isIosDependenciesDestination(destination) || isAndroidDependenciesDestination(destination),
            ),
        [destinationsByStepConfigId],
    );
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const { data, isFetching } = useGetDependenciesQuery(
        { orgId, workflowId: workflowId ?? '', workflowRunId },
        { skip: !auth || !workflowId || !workflowRunId || destinations.length === 0 },
    );
    const dependencies = data?.data;
    const isLoading = isFetching;

    const [modalDestinationId, setModalDestinationId] = React.useState<string | undefined>();
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const iosConfigs: Record<string, IosDependenciesConfig> = React.useMemo(
        () => appSettings?.iosDependenciesConfigs ?? {},
        [appSettings?.iosDependenciesConfigs],
    );
    const androidConfigs: Record<string, AndroidDependenciesConfig> = React.useMemo(
        () => appSettings?.androidDependenciesConfigs ?? {},
        [appSettings?.androidDependenciesConfigs],
    );

    const configuredDestinationIds = React.useMemo(
        () =>
            new Set([
                ...Object.keys(iosConfigs),
                ...Object.keys(androidConfigs),
                ...(dependencies?.reports ?? []).map((report) => report.destinationId),
            ]),
        [iosConfigs, androidConfigs, dependencies?.reports],
    );
    const unconfiguredDestinations = destinations.filter(
        (destination) => !configuredDestinationIds.has(getDestinationId(destination)),
    );

    React.useEffect(() => {
        document.title = 'Dependencies';
    }, []);

    const handleConfigured = React.useCallback(() => setIsModalOpen(false), []);

    const openModal = (destinationId?: string) => {
        setModalDestinationId(destinationId);
        setIsModalOpen(true);
    };

    const modalDestinations =
        modalDestinationId ? destinations.filter((d) => getDestinationId(d) === modalDestinationId)
        : unconfiguredDestinations.length > 0 ? unconfiguredDestinations
        : destinations;

    const hasConfigs = !!dependencies?.hasConfigs;
    const hasLoadedSomething = !!dependencies;

    const destinationById = React.useMemo(() => {
        const map = new Map<string, Destination>();
        for (const destination of destinations) {
            map.set(getDestinationId(destination), destination);
        }
        return map;
    }, [destinations]);

    const entries: PanelEntry[] = React.useMemo(
        () =>
            (dependencies?.reports ?? []).map((destinationReport: DependenciesReleaseReport) => {
                const base = {
                    key: destinationReport.destinationId,
                    destinationId: destinationReport.destinationId,
                    status: destinationReport.status,
                    errorMessage: destinationReport.errorMessage,
                };

                if (destinationReport.platform === 'android') {
                    const report = destinationReport.androidReport;
                    return {
                        ...base,
                        updates: androidUtils.countUpdates(report),
                        total: androidUtils.totalDeps(report),
                        hasReport: !!report,
                        body:
                            report ?
                                <AndroidDependenciesPanelBody
                                    destinationId={destinationReport.destinationId}
                                    report={report}
                                />
                            :   null,
                    };
                }

                const report = destinationReport.iosReport;
                return {
                    ...base,
                    updates: iosUtils.countUpdates(report),
                    total: iosUtils.totalDeps(report),
                    hasReport: !!report,
                    body:
                        report ?
                            <IosDependenciesPanelBody
                                destinationId={destinationReport.destinationId}
                                report={report}
                                config={iosConfigs[destinationReport.destinationId]}
                            />
                        :   null,
                };
            }),
        [dependencies?.reports, iosConfigs],
    );

    const headerLabels = React.useMemo(
        () =>
            buildDestinationHeaderLabels(
                entries.map((entry) => {
                    const destination = destinationById.get(entry.destinationId);
                    return {
                        destinationId: entry.destinationId,
                        destinationName: destination?.name,
                        platform: destination?.platform,
                        appStoreType: destination?.appStoreType,
                    };
                }),
            ),
        [entries, destinationById],
    );

    const defaultActiveKeys =
        entries.length === 1 ?
            [entries[0].key]
        :   entries.filter((entry) => entry.updates > 0).map((entry) => entry.key);

    return (
        <Layout.Content style={{ padding: 24, overflow: 'auto' }}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Space direction="vertical" size={4}>
                        <Text style={titleStyle} type="secondary">
                            Dependencies
                        </Text>
                        <Text>Dependency reports by destination</Text>
                    </Space>
                    {hasConfigs && unconfiguredDestinations.length > 0 && (
                        <Tooltip title={configureDisabledTooltip}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                disabled={configureDisabled}
                                onClick={() => openModal()}
                            >
                                Add destination
                            </Button>
                        </Tooltip>
                    )}
                </Space>

                {!appSettings?.isWorkflowsMigratedAndEnabled || destinations.length === 0 ?
                    <Alert
                        type="warning"
                        message="Dependencies is only available for Flightpaths apps with at least one Apple or Android destination."
                    />
                : !hasLoadedSomething && isLoading ?
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                        <Spin />
                    </div>
                : !hasConfigs ?
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No dependency configuration yet">
                        <Tooltip title={configureDisabledTooltip}>
                            <Button
                                type="primary"
                                icon={<PartitionOutlined />}
                                disabled={configureDisabled}
                                onClick={() => openModal()}
                            >
                                Configure dependencies
                            </Button>
                        </Tooltip>
                    </Empty>
                :   <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Collapse defaultActiveKey={defaultActiveKeys}>
                            {entries.map((entry) => {
                                const summary =
                                    entry.status === 'parsing' ? 'Parsing…'
                                    : entry.status === 'failed' ? 'Parsing failed'
                                    : entry.hasReport ?
                                        `${entry.updates} ${pluralize(entry.updates, 'update', 'updates')} available across ${entry.total} ${pluralize(entry.total, 'dependency', 'dependencies')}`
                                    :   'No report available';
                                return (
                                    <Collapse.Panel
                                        key={entry.key}
                                        header={
                                            <Space size={6}>
                                                <Text strong>{headerLabels[entry.destinationId]}:</Text>
                                                <Text type="secondary">{summary}</Text>
                                            </Space>
                                        }
                                        extra={
                                            entry.status !== 'parsing' ?
                                                <Tooltip title={updateDisabledTooltip}>
                                                    <Button
                                                        size="small"
                                                        icon={<PlusOutlined />}
                                                        disabled={configureDisabled}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openModal(entry.destinationId);
                                                        }}
                                                    >
                                                        Configure
                                                    </Button>
                                                </Tooltip>
                                            :   null
                                        }
                                    >
                                        {entry.status === 'parsing' ?
                                            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                                                <Spin tip="Parsing dependencies…" />
                                            </div>
                                        : entry.status === 'failed' ?
                                            <Alert
                                                type="error"
                                                message={`Dependencies parsing failed: ${entry.errorMessage || 'unknown error'}`}
                                            />
                                        : entry.body ?
                                            entry.body
                                        :   <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="No report has been generated for this destination yet"
                                            />
                                        }
                                    </Collapse.Panel>
                                );
                            })}
                        </Collapse>
                    </Space>
                }
            </Space>
            <ConfigureDependenciesModal
                open={isModalOpen}
                orgId={orgId}
                appId={appId}
                releaseId={releaseId}
                destinations={modalDestinations}
                initialDestinationId={modalDestinationId}
                existingIosConfigs={iosConfigs}
                existingAndroidConfigs={androidConfigs}
                onConfigured={handleConfigured}
                onClose={() => setIsModalOpen(false)}
            />
        </Layout.Content>
    );
};

export default DependenciesPage;
